/**
 * Nmbrs Sync Status Widget
 *
 * Displays the current Nmbrs connection status and provides
 * a manual connection test button. Used in the HR Dashboard.
 *
 * Design system colors:
 * - Navy #2F5A7A: headers, primary
 * - Teal #54949E: secondary info
 * - Green #B7D945: success / connected
 * - Red #D64545: errors only
 */

import { useNmbrsStatus, useTestNmbrsConnection } from '../hooks/useNmbrs';

export function NmbrsSyncStatus() {
  const { data, isLoading, error } = useNmbrsStatus();
  const testConnection = useTestNmbrsConnection();

  const status = data?.data;
  const isConnected = status?.connected === true;
  const isTesting = testConnection.isPending;

  // Determine display state
  const connectionError = testConnection.error || error;
  const testedStatus = testConnection.data?.data;
  const displayStatus = testedStatus || status;
  const displayConnected = testedStatus ? testedStatus.connected : isConnected;

  const handleTestConnection = () => {
    testConnection.mutate();
  };

  return (
    <div className="space-y-6">
      {/* Connection Status Card */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#2F5A7A]">
            Verbindingsstatus
          </h3>
          <button
            onClick={handleTestConnection}
            disabled={isTesting || isLoading}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#2F5A7A] hover:bg-[#254963]"
          >
            {isTesting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Testen...
              </span>
            ) : (
              'Test verbinding'
            )}
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-3 py-4">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#2F5A7A]" />
            <span className="text-sm text-gray-500">Status ophalen...</span>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Connection indicator */}
            <div className="flex items-center gap-3">
              <div
                className={`h-3 w-3 rounded-full ${
                  displayConnected ? 'bg-[#B7D945]' : connectionError ? 'bg-[#D64545]' : 'bg-gray-400'
                }`}
              />
              <span className="text-sm font-medium text-[#1E293B]">
                {displayConnected
                  ? 'Verbonden met Nmbrs'
                  : connectionError
                    ? 'Verbinding mislukt'
                    : 'Niet getest'}
              </span>
            </div>

            {/* Status details */}
            {displayStatus && displayConnected && (
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Omgeving</p>
                  <p className="text-sm font-medium text-[#1E293B]">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        displayStatus.environment === 'production'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {displayStatus.environment === 'production' ? 'Productie' : 'Sandbox'}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Company ID</p>
                  <p className="text-sm font-medium text-[#1E293B]">
                    {displayStatus.companyId}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 mb-1">Laatste check</p>
                  <p className="text-sm text-[#54949E]">
                    {new Date(displayStatus.lastChecked).toLocaleString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>
            )}

            {/* Error message */}
            {connectionError && (
              <div className="mt-3 p-3 rounded-lg text-sm bg-red-50 text-[#D64545]">
                {connectionError instanceof Error
                  ? connectionError.message
                  : 'Er is een fout opgetreden bij het testen van de verbinding.'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info card */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h3 className="text-sm font-semibold mb-3 text-[#2F5A7A]">
          Over de Nmbrs koppeling
        </h3>
        <div className="space-y-2 text-sm text-gray-600">
          <p>
            De Nmbrs koppeling synchroniseert medewerkergegevens tussen het HR-portal en Nmbrs.
          </p>
          <p>
            Gebruik de "Test verbinding" knop om te controleren of de verbinding correct is geconfigureerd.
          </p>
        </div>
      </div>
    </div>
  );
}
